import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Badge, Row, Col, Nav, NavItem, NavLink } from "reactstrap";
import { Table } from "antd";
import dayjs from "dayjs";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../components/Component";
import customerAdminApis from "../../api/customer-admin/customerAdmin";
import { formatCurrency } from "../../utils/Utils";

const paymentStatusColor: Record<string, string> = {
  Approved: "success",
  Pending: "warning",
  Rejected: "danger",
  Available: "success",
  "Partially Used": "info",
  "Fully Used": "secondary",
};

const CustomerPayments = () => {
  const { sm, updateSm } = useOutletContext<any>();
  const [activeTab, setActiveTab] = useState<"history" | "advance">("history");

  // Payment History
  const [payments, setPayments] = useState<any[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [paymentPagination, setPaymentPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Advance Payments
  const [advances, setAdvances] = useState<any[]>([]);
  const [advanceSummary, setAdvanceSummary] = useState<any>(null);
  const [advanceLoading, setAdvanceLoading] = useState(true);
  const [advancePagination, setAdvancePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && currentUser.genericRole !== "admin") {
      navigate(`${process.env.PUBLIC_URL}/dashboard`);
      return;
    }
    fetchPaymentHistory(1, 10);
    fetchAdvancePayments(1, 10);
  }, [currentUser]);

  const fetchPaymentHistory = async (page: number, pageSize: number) => {
    setPaymentLoading(true);
    try {
      const response = await customerAdminApis.fetchPaymentHistory({
        page,
        pageSize,
      });
      if (response?.status && response?.data) {
        setPayments(response.data.data || []);
        setPaymentPagination({
          current: response.data.currentPage || page,
          pageSize: response.data.pageSize || pageSize,
          total: response.data.totalRecords || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchAdvancePayments = async (page: number, pageSize: number) => {
    setAdvanceLoading(true);
    try {
      const response = await customerAdminApis.fetchAdvancePayments({
        page,
        pageSize,
      });
      if (response?.status && response?.data) {
        setAdvanceSummary(response.data.summary || null);
        setAdvances(response.data.list?.data || []);
        setAdvancePagination({
          current: response.data.list?.currentPage || page,
          pageSize: response.data.list?.pageSize || pageSize,
          total: response.data.list?.totalRecords || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch advance payments:", error);
    } finally {
      setAdvanceLoading(false);
    }
  };

  const paymentColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_: any, __: any, index: number) =>
        (paymentPagination.current - 1) * paymentPagination.pageSize + index + 1,
    },
    {
      title: "Payment Number",
      dataIndex: "paymentNumber",
      key: "paymentNumber",
    },
    {
      title: "Type",
      dataIndex: "paymentType",
      key: "paymentType",
      render: (type: string) => (
        <Badge color={type === "Advance" ? "info" : "primary"}>{type || "-"}</Badge>
      ),
    },
    {
      title: "Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (date: string) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Badge color={paymentStatusColor[status] || "secondary"}>{status}</Badge>
      ),
    },
    {
      title: "Invoice",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (val: string) => val || "-",
    },
  ];

  const advanceColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_: any, __: any, index: number) =>
        (advancePagination.current - 1) * advancePagination.pageSize + index + 1,
    },
    {
      title: "Reference No",
      dataIndex: "advanceReferenceNo",
      key: "advanceReferenceNo",
    },
    {
      title: "Date",
      dataIndex: "advanceDate",
      key: "advanceDate",
      render: (date: string) => (date ? dayjs(date).format("DD-MMM-YYYY") : "-"),
    },
    {
      title: "Amount",
      dataIndex: "advanceAmount",
      key: "advanceAmount",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
    {
      title: "Adjusted",
      dataIndex: "adjustedAmount",
      key: "adjustedAmount",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
    {
      title: "Balance",
      dataIndex: "unadjustedBalance",
      key: "unadjustedBalance",
      align: "right" as const,
      render: (val: number) => formatCurrency(val, "INR"),
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (val: string) => val || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Badge color={paymentStatusColor[status] || "secondary"}>{status}</Badge>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Head title="Payments" />
      <BlockHead size="lg">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle tag="h4">Payments</BlockTitle>
            <BlockDes>
              <p>View your payment history and advance payments.</p>
            </BlockDes>
          </BlockHeadContent>
          <BlockHeadContent className="align-self-start d-lg-none">
            <Button
              className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""}`}
              onClick={() => updateSm(!sm)}
            >
              <Icon name="menu-alt-r" />
            </Button>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      {/* Tabs */}
      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            className={activeTab === "history" ? "active" : ""}
            onClick={() => setActiveTab("history")}
            style={{ cursor: "pointer" }}
          >
            Payment History
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeTab === "advance" ? "active" : ""}
            onClick={() => setActiveTab("advance")}
            style={{ cursor: "pointer" }}
          >
            Advance Payments
          </NavLink>
        </NavItem>
      </Nav>

      {/* Payment History Tab */}
      {activeTab === "history" && (
        <Block>
          <Table
            columns={paymentColumns}
            dataSource={payments}
            rowKey="paymentId"
            loading={paymentLoading}
            pagination={{
              current: paymentPagination.current,
              pageSize: paymentPagination.pageSize,
              total: paymentPagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            onChange={(pag) =>
              fetchPaymentHistory(pag.current || 1, pag.pageSize || 10)
            }
            size="middle"
            scroll={{ x: 800 }}
          />
        </Block>
      )}

      {/* Advance Payments Tab */}
      {activeTab === "advance" && (
        <>
          {advanceSummary && (
            <Block>
              <Row className="gy-3">
                <Col md="4">
                  <Card className="card-bordered text-center p-3">
                    <span className="sub-text">Total Advance Paid</span>
                    <span className="lead-text text-primary">
                      {formatCurrency(advanceSummary.totalAdvancePaid, "INR")}
                    </span>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="card-bordered text-center p-3">
                    <span className="sub-text">Total Used</span>
                    <span className="lead-text text-warning">
                      {formatCurrency(advanceSummary.totalUsed, "INR")}
                    </span>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="card-bordered text-center p-3">
                    <span className="sub-text">Available Balance</span>
                    <span className="lead-text text-success">
                      {formatCurrency(advanceSummary.availableBalance, "INR")}
                    </span>
                  </Card>
                </Col>
              </Row>
            </Block>
          )}

          <Block>
            <Table
              columns={advanceColumns}
              dataSource={advances}
              rowKey="advancePaymentId"
              loading={advanceLoading}
              pagination={{
                current: advancePagination.current,
                pageSize: advancePagination.pageSize,
                total: advancePagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={(pag) =>
                fetchAdvancePayments(pag.current || 1, pag.pageSize || 10)
              }
              size="middle"
              scroll={{ x: 800 }}
            />
          </Block>
        </>
      )}
    </React.Fragment>
  );
};

export default CustomerPayments;
